/**
 * The hierarchy of components is as follows:
 * NotificationsOverviewWrapper - queries for artifact collection and triggers data
 * NotificationsOverview - UI that the user interacts with in order to connect/disconnect
 * slack, add/delete notifications
 */
import * as Panel2 from '@wandb/weave/components/Panel2/panel';
import {
  opArtifactId,
  opArtifactName,
  opArtifactProject,
  opDict,
  opEntityName,
  opProjectEntity,
  opProjectName,
} from '@wandb/weave/core';
import {useValue} from '@wandb/weave/react';
import * as React from 'react';

import {
  EventTriggeringConditionType,
  FetchProjectTriggersQueryResult,
  useEntityQuery,
  useFetchProjectTriggersQuery,
} from '../../../../generated/graphql';
import {propagateErrorsContext} from '../../../../util/errors';
import {isUserVisibleTrigger} from '../../../../util/triggers';
import {NotificationTriggerRow} from '../../../ArtifactCollectionSettings/ArtifactCollectionNotifications';
import {useEntityIntegrations} from '../../../ArtifactCollectionSettings/hooks';
import {SectionPlaceholder} from '../../../ArtifactCollectionSettings/SectionPlaceholder';
import {
  getDescription,
  getNotificationTriggers,
} from '../../../ArtifactCollectionSettings/utils';
import {SlackIntegrationsTable} from '../../../SlackIntegration/SlackIntegrationsTable';
import {UserSettingsPlaceholder} from '../../../UserSettings/UserSettingsPlaceholder';
import * as S from './style';

const inputType = 'artifact' as const;
export type ConfigType = {};

export const NotificationsOverviewWrapper: React.FC<
  Panel2.PanelProps<typeof inputType, ConfigType>
> = props => {
  const artifactNode = props.input;
  const overviewValue = useValue(
    React.useMemo(() => {
      const projectNode = opArtifactProject({
        artifact: artifactNode,
      });
      return opDict({
        collectionName: opArtifactName({
          artifact: artifactNode,
        }),
        collectionID: opArtifactId({
          artifact: artifactNode,
        }),
        entityName: opEntityName({
          entity: opProjectEntity({project: projectNode}),
        }),
        projectName: opProjectName({
          project: projectNode,
        }),
      } as any);
    }, [artifactNode])
  );

  const triggersQuery = useFetchProjectTriggersQuery({
    variables: {
      projectName: overviewValue?.result?.projectName,
      entityName: overviewValue?.result?.entityName,
    },
    skip: overviewValue.loading || !overviewValue.result,
    context: propagateErrorsContext(),
  });
  if (
    triggersQuery.loading ||
    triggersQuery.data == null ||
    overviewValue.loading ||
    !overviewValue.result
  ) {
    return <SectionPlaceholder />;
  }
  return (
    <NotificationOverview
      entityName={overviewValue.result.entityName}
      artifactCollectionName={overviewValue.result.collectionName}
      artifactCollectionID={overviewValue.result.collectionID}
      triggersQueryResult={triggersQuery}
    />
  );
};

type NotificationsOverviewProps = {
  entityName: string;
  artifactCollectionName: string;
  artifactCollectionID: string;
  triggersQueryResult: FetchProjectTriggersQueryResult;
};

const NotificationOverview = ({
  entityName,
  artifactCollectionName,
  artifactCollectionID,
  triggersQueryResult,
}: NotificationsOverviewProps) => {
  const entityIntegrations = useEntityIntegrations(entityName);
  const entityQuery = useEntityQuery({
    variables: {
      name: entityIntegrations.entityName,
    },
    skip: entityIntegrations.loading || !entityIntegrations.data,
    fetchPolicy: 'cache-and-network',
  });

  const slackIntegrations = React.useMemo(() => {
    return entityIntegrations.data.filter(i => i.type === 'slack');
  }, [entityIntegrations]);

  // Fetch all notification triggers for this artifact collection
  const notificationTriggers = getNotificationTriggers(
    artifactCollectionID,
    triggersQueryResult
  ).filter(isUserVisibleTrigger);

  if (entityIntegrations.loading) {
    return <SectionPlaceholder />;
  }
  if (entityQuery.loading || !entityQuery.data) {
    return <UserSettingsPlaceholder />;
  }

  const supportedEvents = [EventTriggeringConditionType.LinkModel];
  type triggerEnabled = {
    enabled: boolean;
    id?: string;
    integrationId?: string;
  };
  const enabledNotifications = Object.fromEntries(
    Object.values(EventTriggeringConditionType).map(e => [e, {enabled: false}])
  ) as {
    [T in EventTriggeringConditionType]: triggerEnabled;
  };

  // Note: The backend currently limits one LINK_MODEL notification trigger
  // per artifact collection, and we are relying on this assumption here.
  for (const n of notificationTriggers) {
    const current = n.triggeringCondition.eventType;
    if (n.triggeredAction.__typename === 'NotificationTriggeredAction') {
      enabledNotifications[current] = {
        enabled: true,
        id: n.id,
        integrationId: n.triggeredAction.integration.id,
      };
    }
  }

  return (
    <>
      {entityIntegrations.data.length === 0 ? (
        <S.SettingsOverviewText>
          Notify the team when changes happen in the model registry.
        </S.SettingsOverviewText>
      ) : (
        supportedEvents.map((e, _) => {
          return (
            <div
              key={`styled-${e}`}
              style={{display: 'flex', flexDirection: 'column', width: '100%'}}>
              {(slackIntegrations.length > 0
                ? slackIntegrations
                : [slackIntegrations[0]]
              ).map(integration => (
                <NotificationTriggerRow
                  key={e}
                  eventType={e}
                  eventDescription={getDescription(artifactCollectionName, e)}
                  artifactCollectionID={artifactCollectionID}
                  enabled={
                    enabledNotifications[e]?.enabled &&
                    enabledNotifications[e]?.integrationId === integration.id
                  }
                  triggerID={enabledNotifications[e]?.id}
                  slackIntegration={integration}
                  entityRefetch={entityIntegrations.refetch}
                  triggersQueryResult={triggersQueryResult}
                  artifactCollectionName={artifactCollectionName}
                  isRow={false}
                />
              ))}
            </div>
          );
        })
      )}
      <S.IntegrationOverview>
        <SlackIntegrationsTable
          entity={entityQuery.data.entity}
          entityRefetch={entityQuery.refetch}
          hideCreate={true}
          hideDisconnect={true}
        />
      </S.IntegrationOverview>
    </>
  );
};
